/**
 * SelectResponse — single-choice radio group.
 *
 * Value is the option `key` (not the label). Commits on submit-button
 * click or Enter while a radio is focused.
 */

import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { ResponseCoreProps, ResponseOption } from './types';
import './responses.css';

interface Props extends ResponseCoreProps<string> {
  options: ResponseOption[];
  /** Visual layout: 'stack' (default) renders one option per row;
   *  'chips' wraps options horizontally for short labels. */
  layout?: 'stack' | 'chips';
}

export function SelectResponse({
  label,
  hideLabel,
  initialValue = null,
  onChange,
  onSubmit,
  disabled,
  autoFocus,
  hideSubmit,
  submitLabel = 'Send',
  helper,
  options,
  layout = 'stack',
}: Props) {
  const [value, setValue] = useState<string | null>(initialValue ?? null);
  const firstRadioRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus) firstRadioRef.current?.focus();
  }, [autoFocus]);

  function pick(key: string) {
    setValue(key);
    onChange?.(key);
  }

  function commit(e?: FormEvent) {
    if (e) e.preventDefault();
    if (value === null) return;
    onSubmit?.(value);
  }

  return (
    <form className="cf-response cf-select-response" onSubmit={commit}>
      <fieldset
        className={
          layout === 'chips'
            ? 'cf-response-fieldset cf-select-response__fieldset--chips'
            : 'cf-response-fieldset'
        }
        disabled={disabled}
      >
        <legend className={hideLabel ? 'cf-visually-hidden' : 'cf-response__label'}>
          {label}
        </legend>
        {options.map((opt, i) => (
          <label
            key={opt.key}
            className={
              value === opt.key
                ? 'cf-response-option cf-response-option--selected'
                : 'cf-response-option'
            }
          >
            <input
              ref={i === 0 ? firstRadioRef : undefined}
              type="radio"
              name={`cf-select-${label}`}
              value={opt.key}
              checked={value === opt.key}
              onChange={() => pick(opt.key)}
              className="cf-response-option__native"
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </fieldset>
      {helper && <div className="cf-response__helper">{helper}</div>}
      {!hideSubmit && (
        <div className="cf-response__actions">
          <button
            type="submit"
            className="cf-btn cf-btn--primary"
            disabled={disabled || value === null}
          >
            {submitLabel}
          </button>
        </div>
      )}
    </form>
  );
}
